import { Dialog, ErrorMessage } from '@koopajs/mui';
import {
  useComponentVisibility,
  useResourceCreate,
  useFileUpload,
  useSearchParams,
  useWorkspaceShow
} from '@koopajs/react';
import { Button, Box, Typography } from '@mui/material';
import { useLocale } from '@koopajs/react';
import { useCallback } from 'react';
import prettyBytes from 'pretty-bytes';
import { RenderDocumentIcon } from 'components/RenderDocumentIcon';
import { ICommittee } from 'types';
import { transformDocumentRecordDataForm } from 'utils/transformDocumentRecordDataForm';
import { DocumentRecordFormFields } from 'components/DocumentRecords/DocumentRecordFormFields';
import { useSnackbar } from 'notistack';
import { fileUploadSnackbar } from 'utils/DocumentRecords/fileUploadSnackbar';

export const AddDocumentRecordDialog: React.FC = () => {
  const { t } = useLocale();

  const keyPrefix = 'Dialogs.AddDocumentRecord';
  const fileUpload = useFileUpload();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const addDocument = useComponentVisibility('addDocumentRecordDialog');
  const { blobUrl, file, committees, isCommitteeFieldDisabled, isCategoryFieldDisabled } =
    addDocument.getContext() as {
      blobUrl: string;
      file: File;
      committees: ICommittee[];
      defaultValues: { lastDocumentTitle: string | undefined };
      isCommitteeFieldDisabled?: boolean;
      isCategoryFieldDisabled?: boolean;
    };

  const committeesOptions = committees?.map((c) => ({ id: c.id as string, label: c.name as string })) || [];

  const { createResource, errorMessage } = useResourceCreate({
    path: '/document-records'
  });

  const handleSubmit = useCallback(
    async (data: object): Promise<boolean> => {
      const fileUri = await fileUpload.upload(file);

      const payload = transformDocumentRecordDataForm({ data, file, fileUri });

      const isSuccess = await createResource(payload);

      if (isSuccess) {
        addDocument.setHidden(); // manually setting to hidden for scroll

        await new Promise((resolve) => setTimeout(resolve, 1)); // scroll is only working when we do this

        document?.querySelector(`#documents-table`)?.scrollIntoView({ behavior: 'smooth' });
      }

      fileUploadSnackbar({
        isSuccess,
        file,
        trackEvent: 'document-record',
        enqueueSnackbar,
        closeSnackbar,
        successMessage: t('common:snackbarFileUploadSuccess'),
        failMessage: t('common:snackbarFileUploadFail')
      });

      return isSuccess;
    },
    [createResource]
  );

  return (
    <>
      <Dialog.Form
        dialogKey="addDocumentRecordDialog"
        onSubmit={handleSubmit}
        i18n={{ keyPrefix: keyPrefix + '.DialogForm' }}
        maxWidth="md"
        leftAction={<Button onClick={addDocument.setHidden}>{t('common:labelCancel')}</Button>}
        dialogProps={{ PaperProps: { 'data-cy': 'add-document-record-dialog' } }}
      >
        <ErrorMessage error={errorMessage} />
        <Box
          sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: { xs: 1, lg: 3 }, mt: 2 }}
        >
          {blobUrl && (
            <iframe
              title={t('common:fileViewer')}
              data-cy="add-document-record-dialog_file-iframe"
              src={blobUrl}
              style={{ width: '100%', height: '500px' }}
              className="rr-block"
            />
          )}
          <Box sx={{ width: '100%', minWidth: '0px' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 2
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  overflow: 'hidden'
                }}
              >
                <RenderDocumentIcon fileType={file?.type} sx={{ mr: 1 }} />
                <Typography
                  variant="body2"
                  display="inline"
                  sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  data-cy={'add-document-record-dialog_file-name'}
                  className="rr-mask"
                >
                  {file?.name}
                </Typography>
              </Box>

              <span>&#183;</span>
              <Typography variant="body2" sx={{ opacity: 0.6, flex: 'none' }}>
                {file?.size && prettyBytes(file.size)}
              </Typography>
            </Box>
            <DocumentRecordFormFields
              committeesOptions={committeesOptions}
              committeeFieldState={isCommitteeFieldDisabled ? 'disabled' : undefined}
              categoryFieldState={isCategoryFieldDisabled ? 'disabled' : undefined}
            />
          </Box>
        </Box>
      </Dialog.Form>
    </>
  );
};
